import { StanModal, StanProgressBar } from '@/components';
import { useEffect, useState } from 'react';
import { exportOrdersToCSV } from '../lib/orders.logic';
import { createIconElement, SENTRY_FEATURES, stanNotify } from '@/lib';
import IcBox from '@/assets/icons/ic-box.svg';

interface ExportOrdersModalProps {
    isOpen: boolean;
    onClose: () => void;
    backendFilterValues?: string;
    userFanId?: number;
}

export function ExportOrdersModal({ isOpen, onClose, backendFilterValues, userFanId }: ExportOrdersModalProps) {
    const [exportingPage, setExportingPage] = useState(0);
    const [totalExportPages, setTotalExportPages] = useState(0);
    const [isExporting, setIsExporting] = useState(true);

    // Generate filename similar to Vue version
    const excelFilename = (() => {
        const today = new Date();
        return `Your Stan Transactions - ${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}.csv`;
    })();

    useEffect(() => {
        if (isOpen) {
            resetState();
            handleExport();
        }
    }, [isOpen]);

    const resetState = () => {
        setIsExporting(true);
        setExportingPage(0);
        setTotalExportPages(0);
    };

    const handleExport = async () => {
        try {
            await exportOrdersToCSV(backendFilterValues, userFanId, (pagesFetched, total) => {
                // Vue uses exportingPage which tracks pages fetched (0-based for progress bar)
                setExportingPage(pagesFetched);
                setTotalExportPages(total);
            });

            // Close modal after a short delay (like Vue version)
            setTimeout(() => {
                onClose();
            }, 500);
        } catch (error) {
            stanNotify({
                type: 'error',
                title: 'Exporting Transactions could not be generated, let us help you with that!',
                text: 'Please email friends@stanwith.me',
                duration: 4000,
                error: {
                    message: '[INCOME] failed-to-export-orders',
                    options: {
                        tags: { feature: SENTRY_FEATURES.INCOME },
                        context: { error },
                    },
                },
            });
            setTimeout(() => {
                onClose();
            }, 500);
        }
    };

    const finishDownload = () => {
        setIsExporting(false);
    };

    const BoxIcon1 = createIconElement(IcBox, {
        className: 'm-1 size-20 animate-bounce translate-x-12 rotate-6 duration-1000 ease-in-out',
    });
    const BoxIcon2 = createIconElement(IcBox, { className: 'h-52 w-52 animate-bounce ease-in-out' });
    const BoxIcon3 = createIconElement(IcBox, {
        className: 'm-1 h-16 w-16 animate-bounce -translate-x-12 -rotate-6 duration-300 ease-in-out',
    });

    return (
        <StanModal
            id='export-orders-modal'
            isOpen={isOpen}
            onClose={onClose}
            title='Exporting Transactions'
            footerNote={excelFilename}
            hideCloseButton
            primaryActionLabel='Finish'
            onPrimaryAction={finishDownload}
            primaryActionDisabled={isExporting}
            className='duration-1000 ease-in-out'
        >
            <div>
                <div className='flex items-center justify-center p-[38px]'>
                    {BoxIcon1}
                    {BoxIcon2}
                    {BoxIcon3}
                </div>
                <StanProgressBar min={0} max={totalExportPages} value={exportingPage} />
            </div>
        </StanModal>
    );
}
